import { readChatTurns } from "@/lib/chat-turn-history";
import {admitEditorAttachments,editorTurnError,EditorSubmissionGate, type EditorSubmission} from "@/lib/editor-submission";
import {restoreChatTools} from "@/lib/chat-tool-history";
import { operation } from "@/lib/api-client";
import { streamChatTurn, cancelChatTurn, type ChatStartInput } from "@/lib/chat-turn-stream";
import { applyChatProgress } from "@/lib/chat-progress";
import { recoverChatMessages, type DurableChatTurn } from "@/lib/chat-recovery";
import { readAllRows } from "@/lib/read-all-rows";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, ArrowLeft, ChevronDown, ChevronUp, Download, Loader2, Paperclip, RotateCcw, Send, Square } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChatMessages, type ChatMessage } from "@/components/chat/chat-messages";
import { aws as awsClient, getCsrfToken } from "@/aws/client";
import { getSignedUrl, uploadChatFile, MAX_FILE_BYTES, MAX_FILES_PER_MESSAGE, type UploadedAttachment } from "@/lib/upload";
import { AttachmentChips, type ChipItem } from "@/components/chat/attachment-chips";
import { unwrap } from "@/lib/server-fn";
import { addImageItem, getImageSession, restoreImageVersion } from "@/features/images/image.functions";
import { editorVersion } from "@/lib/editor-version";
import { useEditorRestore } from "@/hooks/use-editor-restore";

export const Route = createFileRoute("/images/$sessionId")({
  component: ImageWorkspace,
  validateSearch: (s: Record<string, unknown>) => ({
    item: typeof s.item === "string" ? s.item : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Image editor — AskBell" },
      { name: "description", content: "Edit and create images from a chat with the AskBell agent." },
      { property: "og:title", content: "Image editor — AskBell" },
      { property: "og:description", content: "Edit and create images from a chat with the AskBell agent." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});


type SessionInfo = {
  id: string;
  title: string;
  conversation_id: string;
  source_conversation_id?: string | null;
};
type ItemRow = { id: string; position: number; label: string; mime_type: string; current_version: number };
type VersionRow = {
  item_id: string;
  version: number;
  storage_path: string;
  mime_type: string;
  size: number;
  prompt: string | null;
  created_by: string;
  created_at: string;
};
type PendingAttachment = {
  id: string;
  name: string;
  size: number;
  type: string;
  uploading: boolean;
  error?: string;
  uploaded?: UploadedAttachment;
};

function ImageWorkspace() {
  const { sessionId } = Route.useParams();
  const { item: focusParam } = Route.useSearch();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionInfo | null>(null);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [marks, setMarks] = useState<Map<string, "added" | "edited">>(new Map());
  const { restoring, restore } = useEditorRestore();
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const focusedRef = useRef(false);
  const submissionGate = useRef(new EditorSubmissionGate());
  const activeRef = useRef<{ controller: AbortController; turnId?: string; cancelled: boolean } | null>(null);
  const turnsRef = useRef<DurableChatTurn[]>([]);
  const sessionRef = useRef(sessionId);
  const refreshRequest = useRef(0);
  const messageRequest = useRef(0);
  sessionRef.current = sessionId;
  useEffect(() => () => { ++refreshRequest.current; ++messageRequest.current; submissionGate.current.reset(); activeRef.current?.controller.abort(); activeRef.current = null; }, [sessionId]);


  // Auto-scroll (same behavior as the deck chat).
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const stickToBottomRef = useRef(true);
  const isNearBottom = useCallback(() => {
    const v = scrollViewportRef.current;
    if (!v) return true;
    return v.scrollHeight - v.scrollTop - v.clientHeight <= 80;
  }, []);
  const scrollToBottom = useCallback(() => {
    if (!stickToBottomRef.current) return;
    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    scrollFrameRef.current = requestAnimationFrame(() => {
      const v = scrollViewportRef.current;
      if (v) v.scrollTop = v.scrollHeight;
      scrollFrameRef.current = null;
    });
  }, []);
  useEffect(() => {
    const v = scrollViewportRef.current;
    if (!v) return;
    const onScroll = () => { stickToBottomRef.current = isNearBottom(); };
    v.addEventListener("scroll", onScroll, { passive: true });
    return () => v.removeEventListener("scroll", onScroll);
  }, [isNearBottom]);
  useLayoutEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => {
    const c = scrollContentRef.current;
    if (!c || typeof ResizeObserver === "undefined") return;
    const o = new ResizeObserver(() => scrollToBottom());
    o.observe(c);
    return () => o.disconnect();
  }, [scrollToBottom]);

  const versionsFor = useCallback(
    (itemId: string) => versions.filter((v) => v.item_id === itemId).sort((a, b) => b.version - a.version),
    [versions],
  );

  const refresh = useCallback(async () => {
    const request = ++refreshRequest.current;
    const current = () => sessionRef.current === sessionId && request === refreshRequest.current;
    try {
      const res = unwrap<{ session: SessionInfo; items: ItemRow[]; versions: VersionRow[] }>(
        await getImageSession({ data: { sessionId } }),
      );
      if (!current()) return null;
      setLoadError(null);
      setSession(res.session);
      setItems(res.items ?? []);
      setVersions(res.versions ?? []);
      // Sign every version path we don't have yet.
      const missing = (res.versions ?? []).filter((v) => !urls.has(v.storage_path));
      if (missing.length) {
        const signed = await Promise.all(
          missing.map(async (v) => [v.storage_path, (await getSignedUrl(v.storage_path, 7200)) ?? ""] as const),
        );
        if (!current()) return null;
        setUrls((prev) => {
          const next = new Map(prev);
          for (const [p, u] of signed) if (u) next.set(p, u);
          return next;
        });
      }
      return res;
    } catch (e: any) {
      if (current()) setLoadError(e?.message ?? "Could not open this image session.");
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadMessages = useCallback(async (conversationId: string) => {
    const request = ++messageRequest.current;
    const current = () => sessionRef.current === sessionId && request === messageRequest.current;
    const [msgs, atts, result] = await Promise.all([
      readAllRows<any>((from,to) => awsClient.from("messages").select("id,role,content,created_at,error,reasoning").eq("conversation_id",conversationId).order("created_at").order("id").range(from,to),current),
      readAllRows<any>((from,to) => awsClient.from("attachments").select("id,message_id,filename,mime_type,size,storage_path,source").eq("conversation_id",conversationId).order("created_at").order("id").range(from,to),current),
      readChatTurns(conversationId,current),
    ]);
    if (!current()) return null;
    if (msgs.error || atts.error) throw new Error((msgs.error ?? atts.error)!.message);
    const cards = await Promise.all((atts.data ?? []).filter(a=>a.message_id).map(async a=>({...a,signed_url:await getSignedUrl(a.storage_path) ?? undefined})));
    if (!current()) return null;
    const hydrated: ChatMessage[] = (msgs.data ?? []).map(m=>({...m,attachments:cards.filter(a=>a.message_id===m.id&&a.source!=='generated'),generated:cards.filter(a=>a.message_id===m.id&&a.source==='generated')}));
    const withTools=await restoreChatTools(recoverChatMessages(hydrated,result.turns),conversationId,current);
    if(!current())return null;
    turnsRef.current = result.turns;
    setMessages(withTools);
    return {messages:hydrated,turns:result.turns};
  }, [sessionId]);

  const observe = async (body:ChatStartInput, assistantId:string, userId:string, resume?:{turnId:string;conversationId:string;messageId:string}, retry?:{turnId:string;clientRequestId:string}, onAccepted?:()=>void, submission?:EditorSubmission) => {
    ++messageRequest.current;
    const active = {controller:new AbortController(),turnId:resume?.turnId,cancelled:false};
    activeRef.current=active; setIsStreaming(true);
    const owns=()=>sessionRef.current===sessionId&&activeRef.current===active&&!active.controller.signal.aborted;
    const update=(fn:(m:ChatMessage)=>ChatMessage)=>{if(owns())setMessages(prev=>prev.map(m=>m.id===assistantId?fn(m):m));};
    try {
      await streamChatTurn({body,resume,retry,signal:active.controller.signal,cancelRequested:()=>active.cancelled,
        onTurnId:id=>{active.turnId=id;if(submission)submissionGate.current.accepted(submission);},
        onStarted:accepted=>{if(owns()){onAccepted?.();setMessages(prev=>prev.map(m=>m.id===userId?{...m,id:accepted.messageId}:m));}},
        onEvent:event=>{
          if(!owns())return;
          if(event.type==='error') update(m=>({...m,error:editorTurnError(event,'The response could not be completed.')}));
          else update(m=>applyChatProgress(m,event));
          if(event.type==='tool_result'&&['image_edit','image_create'].includes(event.name)&&!event.error){
            if(typeof event.itemId==='string'){setMarks(prev=>new Map(prev).set(event.itemId,event.name==='image_create'?'added':'edited'));setSelectedId(event.itemId);setViewVersion(null);}
            void refresh();
          }
        },
        onFinal:async()=>{if(owns()){await refresh();await loadMessages(body.conversationId!);}},
      });
      if(owns()){await refresh();await loadMessages(body.conversationId!);}
    } catch(error:any) {
      if(owns())update(m=>({...m,error:{headline:error?.message??'The image agent failed.',retryable:false}}));
    } finally {if(activeRef.current===active){activeRef.current=null;setIsStreaming(false);}}
  };
  const observeRef=useRef(observe); observeRef.current=observe;

  useEffect(() => {
    let cancelled=false;
    setLoadError(null);setSession(null);setMessages([]);setPending([]);setInput('');setIsStreaming(false);focusedRef.current=false;
    (async()=>{
      const res=await refresh();if(cancelled||!res)return;
      const focus=res.items.find(i=>i.id===focusParam)??res.items[0];if(focus)setSelectedId(focus.id);
      const loaded=await loadMessages(res.session.conversation_id);if(cancelled||!loaded)return;
      const running=loaded.turns.find(t=>t.canManage===true&&['queued','running','waiting'].includes(t.status));
      if(running&&!activeRef.current){
        const original=loaded.messages.find(m=>m.id===running.userMessageId);if(!original)throw new Error('The active turn has no saved user message.');
        setMessages(prev=>[...prev.filter(m=>m.id!==running.assistantMessageId),{id:running.assistantMessageId,role:'assistant',content:'',created_at:original.created_at}]);
        void observeRef.current({conversationId:res.session.conversation_id,clientRequestId:running.turnId,prompt:original.content,attachmentIds:[],modelId:'image-editor',imageSessionId:sessionId},running.assistantMessageId,running.userMessageId,{turnId:running.turnId,conversationId:res.session.conversation_id,messageId:running.userMessageId});
      }
    })().catch(error=>{if(!cancelled)setLoadError(error.message);});
    return()=>{cancelled=true;};
  },[refresh,loadMessages,focusParam,sessionId]);

  const stop = async () => {
    const active=activeRef.current;if(!active){submissionGate.current.cancel();setIsStreaming(false);return;}
    active.cancelled=true;
    try {if(active.turnId)await cancelChatTurn(active.turnId);active.controller.abort();if(session){await refresh();await loadMessages(session.conversation_id);}}
    catch(error:any){toast.error(error.message??'Could not stop the turn.');}
  };
  const retry = async (message:ChatMessage) => {
    if(!session||isStreaming)return;
    const turn=turnsRef.current.find(t=>t.assistantMessageId===message.id);
    if(!turn?.retryable||turn.reconciliationRequired)return;
    setMessages(prev=>prev.map(m=>m.id===message.id?{...m,content:'',tools:[],error:null}:m));
    const requestId=crypto.randomUUID();
    await observe({conversationId:session.conversation_id,clientRequestId:requestId,prompt:'',attachmentIds:[],modelId:'image-editor',imageSessionId:sessionId},message.id,turn.userMessageId,undefined,{turnId:turn.turnId,clientRequestId:requestId});
  };

  useEffect(() => { composerRef.current?.focus(); }, [sessionId, isStreaming]);

  // Reset to the latest version whenever the selection changes.
  useEffect(() => { setViewVersion(null); setHistoryOpen(false); }, [selectedId]);

  // Keyboard rail navigation.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName.toLowerCase();
      if (tag === "textarea" || tag === "input" || tag === "select" || t.isContentEditable) return;
      if (!items.length) return;
      const idx = Math.max(0, items.findIndex((i) => i.id === selectedId));
      let next = idx;
      if (["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key)) next = Math.max(0, idx - 1);
      else if (["ArrowDown", "ArrowRight", "PageDown"].includes(e.key)) next = Math.min(items.length - 1, idx + 1);
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = items.length - 1;
      else return;
      e.preventDefault();
      if (next !== idx) setSelectedId(items[next].id);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    const el = document.querySelector(`[data-item-id="${selectedId}"]`) as HTMLElement | null;
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedId]);

  const selected = useMemo(() => items.find((i) => i.id === selectedId) ?? items[0] ?? null, [items, selectedId]);
  const selectedVersions = selected ? versionsFor(selected.id) : [];
  const shownVersion = editorVersion(versions, selected, viewVersion);
  useEffect(() => { setPreviewError(null); }, [shownVersion?.storage_path]);
  const currentUrlFor = (it: ItemRow) => {
    const v = editorVersion(versions, it, null);
    return v ? urls.get(v.storage_path) : undefined;
  };
  const retryPreview = useCallback(async (version: VersionRow) => {
    setPreviewError(null);
    const url = await getSignedUrl(version.storage_path, 7200);
    if (!url) { setPreviewError("A fresh preview link could not be created."); return; }
    setUrls((previous) => new Map(previous).set(version.storage_path, url));
  }, []);

  const chips: ChipItem[] = pending.map((p) => ({
    id: p.id, name: p.name, size: p.size, type: p.type, uploading: p.uploading, error: p.error,
    extractionError: p.uploaded?.extraction_error ?? undefined,
  }));

  const handleFiles = useCallback(async (files: File[]) => {
    if (!session || !files.length) return;
    const admission=admitEditorAttachments(pending.length,files.length,MAX_FILES_PER_MESSAGE);
    if (!admission.acceptedCount) return toast.error(`Max ${MAX_FILES_PER_MESSAGE} files per message.`);
    if(admission.skippedCount)toast.error(`Max ${MAX_FILES_PER_MESSAGE} files per message — ${admission.skippedCount} skipped.`);
    const { data: { session: auth } } = await awsClient.auth.getSession();
    const userId = auth?.user?.id;
    if (!userId) return toast.error("Your session expired — sign in again.");
    for (const file of files.slice(0, admission.acceptedCount)) {
      const id = crypto.randomUUID();
      if (file.size > MAX_FILE_BYTES) { toast.error(`${file.name} is too large.`); continue; }
      setPending((prev) => [...prev, { id, name: file.name, size: file.size, type: file.type, uploading: true }]);
      try {
        const uploaded = await uploadChatFile(file, userId, session.conversation_id);
        setPending((prev) => prev.map((p) => (p.id === id ? { ...p, uploading: false, uploaded } : p)));
      } catch (e: any) {
        setPending((prev) => prev.map((p) => (p.id === id ? { ...p, uploading: false, error: e?.message ?? "Upload failed" } : p)));
      }
    }
  }, [session, pending.length]);

  const handleDownload = async () => {
    if (!shownVersion || !selected) return;
    const url = urls.get(shownVersion.storage_path) ?? (await getSignedUrl(shownVersion.storage_path, 3600));
    if (!url) return toast.error("Could not prepare the download.");
    const a = document.createElement("a");
    a.href = url;
    a.download = selected.label.replace(/(\.[a-z0-9]+)?$/i, `-v${shownVersion.version}$1`);
    a.click();
  };

  const handleRestore = async (version: number) => {
    if (!selected) return;
    try {
      const done = await restore(
        selected.id,
        version,
        selected.current_version,
        (request) => restoreImageVersion({ data: { itemId: selected.id, ...request } }),
        async () => { setViewVersion(null); return refresh(); },
      );
      if (done) toast.success(`Restored version ${version}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Restore failed.");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!session || isStreaming) return;
    const ready = pending.filter((p) => p.uploaded && !p.error);
    if (!text && !ready.length) return;
    if (pending.some((p) => p.uploading)) return toast.error("Wait for the files to finish uploading.");
    if (pending.some((p) => p.error || !p.uploaded?.id)) return toast.error("Remove or retry failed attachments before sending.");
    const submission=submissionGate.current.begin(JSON.stringify({sessionId,text,files:ready.map(p=>p.uploaded!.id),selected:selected?.position??null}));
    if(!submission)return;
    const current=()=>sessionRef.current===sessionId&&submissionGate.current.owns(submission);
    setIsStreaming(true); setMarks(new Map());
    try {
    // File contents are reconstructed from verified server metadata.
    let augmented = "";
    const addedNotes: string[] = [];
    for (const p of ready) {
      const u = p.uploaded!;
      if ((u.mime_type ?? '').startsWith('image/')) {
        try {
          const r = unwrap<{ itemId: string; position?: number; created: boolean }>(
            await addImageItem({ data: { sessionId, storagePath: u.storage_path, filename: u.filename, mimeType: u.mime_type, size: u.size } }),
          );
          if(!current())return;
          if (r.created) addedNotes.push(`${u.filename} (image ${r.position})`);
        } catch (e: any) {
          if(!current())return;
          toast.error(`Could not add ${u.filename}: ${e?.message ?? "unknown error"}`);
          setIsStreaming(false);
          return;
        }
        continue;
      }
    }
    if (addedNotes.length) {
      augmented += `\n\n(The user just added new images to the stack: ${addedNotes.join(", ")}.)`;
      await refresh();
    }

    if(!current())return;
    const now = new Date().toISOString();
    const userMsg: ChatMessage = {
      id: submission.requestId, role: "user",
      content: text || `Attached ${ready.length} file${ready.length === 1 ? "" : "s"}.`,
      created_at: now,
      attachments: ready.map((p) => ({
        id: p.uploaded!.id ?? p.id, filename: p.uploaded!.filename, mime_type: p.uploaded!.mime_type,
        size: p.uploaded!.size, storage_path: p.uploaded!.storage_path,
      })) as any,
    };
    const assistantId = submission.assistantId;
    setMessages((prev) => [...prev.filter(m=>m.id!==userMsg.id&&m.id!==assistantId), userMsg, { id: assistantId, role: "assistant", content: "", created_at: now }]);
    await observe({conversationId:session.conversation_id,clientRequestId:userMsg.id,prompt:userMsg.content,attachmentIds:ready.map(p=>p.uploaded!.id!),modelId:'image-editor',imageSessionId:session.id,imageSelectedItem:selected?.position??null,...(augmented?{augmentedContent:augmented}:{})},assistantId,userMsg.id,undefined,undefined,()=>{if(current()){setInput('');setPending([]);}},submission);
    } finally {const wasCurrent=current();submissionGate.current.finish(submission);if(wasCurrent)setIsStreaming(false);}
  };

  if (loadError) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center text-ui-base text-muted-foreground">{loadError}</div>
      </AppLayout>
    );
  }

  const shownUrl = shownVersion ? urls.get(shownVersion.storage_path) : undefined;

  return (
    <AppLayout>
      <div className="flex h-full flex-col">
        <header className="flex items-center gap-3 border-b border-border px-4 py-2.5">
          <Button
            size="icon" variant="ghost" className="h-8 w-8"
            onClick={() => session?.source_conversation_id ? navigate({ to: "/chat", search: { conversation: session.source_conversation_id } }) : navigate({ to: "/editors" })}
            aria-label={session?.source_conversation_id ? "Back to the chat these images came from" : "Back to editors"}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-ui-base font-bold text-foreground">{session?.title ?? "Images"}</h1>
            <p className="text-ui-sm text-muted-foreground">
              {selected ? shownVersion
                ? `Image ${selected.position} of ${items.length} · ${selected.label} · v${shownVersion.version}`
                : `Image ${selected.position} of ${items.length} · ${selected.label} · version unavailable`
                : "Loading…"}
            </p>
          </div>
          <Button size="sm" variant="secondary" className="gap-1.5 text-ui-sm" onClick={handleDownload} disabled={!shownVersion}>
            <Download className="h-3.5 w-3.5" /> Download
          </Button>
        </header>

        <div className="flex min-h-0 flex-1">
          {/* Image rail */}
          <aside className="w-44 shrink-0 overflow-y-auto border-r border-border bg-background/40 p-2">
            {items.length === 0 && <div className="p-3 text-ui-sm text-muted-foreground">No images yet.</div>}
            {items.map((it) => {
              const u = currentUrlFor(it);
              const mark = marks.get(it.id);
              return (
                <button
                  key={it.id}
                  data-item-id={it.id}
                  onClick={() => setSelectedId(it.id)}
                  className={`relative mb-2 block w-full overflow-hidden rounded border text-left transition ${
                    it.id === selected?.id ? "border-primary ring-1 ring-primary" : "border-border hover:border-muted-foreground/50"
                  }`}
                >
                  <div className="aspect-square w-full bg-secondary/40">
                    {u ? <img src={u} alt={it.label} loading="lazy" className="h-full w-full object-cover" /> : <div className="h-full w-full animate-pulse bg-secondary" />}
                  </div>
                  {mark && (
                    <span
                      title={mark === "added" ? "Added this turn" : "Edited this turn"}
                      className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-ui-success-bg text-ui-sm font-bold leading-none text-ui-success-icon shadow"
                    >
                      {mark === "added" ? "+" : "*"}
                    </span>
                  )}
                  <span className="block truncate px-1.5 py-1 text-ui-xs text-muted-foreground" title={it.label}>
                    {it.position}. {it.label}
                  </span>
                </button>
              );
            })}
          </aside>

          {/* Selected image + version history */}
          <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background/20">
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6">
              {shownUrl ? (
                <img
                  src={shownUrl}
                  alt={selected?.label ?? ""}
                  className="max-h-full max-w-full rounded border border-border "
                  onError={() => {
                    if (shownVersion) setUrls((previous) => { const next = new Map(previous); next.delete(shownVersion.storage_path); return next; });
                    setPreviewError("The preview link expired or the image could not be loaded.");
                  }}
                />
              ) : shownVersion ? (
                <div className="max-w-md rounded-ui-md border border-destructive/30 bg-destructive/5 p-5 text-center">
                  <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
                  <p className="text-ui-base font-bold text-foreground">Could not display this image</p>
                  <p className="mt-1 text-ui-sm text-muted-foreground">{previewError ?? "The preview link is unavailable."}</p>
                  <Button className="mt-4" size="sm" variant="outline" onClick={() => retryPreview(shownVersion)}>Retry preview</Button>
                </div>
              ) : selected ? (
                <div className="max-w-md rounded-ui-md border border-destructive/30 bg-destructive/5 p-5 text-center">
                  <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
                  <p className="text-ui-base font-bold text-foreground">This image version is unavailable</p>
                  <p className="mt-1 text-ui-sm text-muted-foreground">Reload the editor to refresh its version history.</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-ui-base text-muted-foreground">
                  Nothing to show yet.
                </div>
              )}
            </div>
            {selected && (
              <div className="shrink-0 border-t border-border bg-background/40">
                <button
                  type="button"
                  onClick={() => setHistoryOpen((v) => !v)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-ui-sm text-muted-foreground hover:text-foreground"
                  aria-expanded={historyOpen}
                >
                  <span className="font-bold">{selectedVersions.length} version{selectedVersions.length === 1 ? "" : "s"}</span>
                  {viewVersion !== null && viewVersion !== selected.current_version && (
                    <span className="rounded bg-secondary px-1.5 py-0.5">viewing v{viewVersion}</span>
                  )}
                  <span className="ml-auto flex items-center gap-1">
                    {historyOpen ? "Hide" : "Show"}
                    {historyOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
                  </span>
                </button>
                {historyOpen && (
                  <div className="flex gap-3 overflow-x-auto border-t border-border px-4 py-3">
                    {selectedVersions.map((v) => {
                      const u = urls.get(v.storage_path);
                      const isCurrent = v.version === selected.current_version;
                      const isShown = v.version === (viewVersion ?? selected.current_version);
                      return (
                        <div key={v.version} className="w-36 shrink-0">
                          <button
                            type="button"
                            onClick={() => setViewVersion(v.version)}
                            className={`block w-full overflow-hidden rounded border ${isShown ? "border-primary ring-1 ring-primary" : "border-border hover:border-muted-foreground/50"}`}
                            title={v.prompt ?? ""}
                          >
                            <div className="aspect-square bg-secondary/40">
                              {u ? <img src={u} alt={`v${v.version}`} className="h-full w-full object-cover" /> : <div className="h-full w-full animate-pulse bg-secondary" />}
                            </div>
                          </button>
                          <div className="mt-1 flex items-center justify-between text-ui-xs text-muted-foreground">
                            <span>v{v.version}{isCurrent ? " · current" : ""}</span>
                            {!isCurrent && (
                              <button
                                type="button"
                                className="flex items-center gap-1 hover:text-foreground"
                                onClick={() => handleRestore(v.version)}
                                disabled={restoring}
                                title="Make this the current version"
                              >
                                <RotateCcw className="h-3 w-3" /> Restore
                              </button>
                            )}
                          </div>
                          <p className="line-clamp-2 text-ui-xs text-muted-foreground/80">{v.prompt}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </main>

          {/* Agent chat */}
          <section className="flex w-[400px] shrink-0 flex-col border-l border-border">
            <div ref={scrollViewportRef} className="min-h-0 flex-1 overflow-y-auto">
              <div ref={scrollContentRef}>
                {messages.length === 0 ? (
                  <div className="p-4 text-ui-base text-muted-foreground">
                    Describe how to change the selected image — “make the background a soft gradient”, “put the logo from
                    image 2 in the corner” — or ask for a brand-new image. Every change is saved as a new version.
                  </div>
                ) : (
                  <ChatMessages messages={messages} isStreaming={isStreaming} onRetryMessage={(id) => {const message=messages.find(m=>m.id===id);if(message)void retry(message);}} />
                )}
              </div>
            </div>
            <div className="border-t border-border p-3">
              <AttachmentChips items={chips} onRemove={(id) => setPending((prev) => prev.filter((p) => p.id !== id))} />
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef} type="file" multiple className="hidden"
                  onChange={(e) => { void handleFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }}
                />
                <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => fileInputRef.current?.click()} disabled={isStreaming} aria-label="Attach files">
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Textarea
                  ref={composerRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
                  placeholder={selected ? `Edit image ${selected.position}, or ask for a new one…` : "Describe the change…"}
                  className="max-h-40 min-h-[60px] resize-none text-ui-base"
                  disabled={isStreaming}
                />
                <Button
                  size="icon" className="h-9 w-9 shrink-0" onClick={() => void (isStreaming ? stop() : send())}
                  aria-label={isStreaming ? "Stop response" : "Send message"}
                  disabled={!isStreaming && (!input.trim() && !pending.some((p) => p.uploaded && !p.error))}
                >
                  {isStreaming ? <Square className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
